/**
 * ChatTimerManager - Singleton service for managing chat request timers
 *
 * This service ensures timers start immediately when AWAITING_USER_INPUT events
 * are received, regardless of whether the chat UI component is mounted.
 *
 * The timer manager is the single source of truth for all timer state.
 */

import * as Sentry from '@sentry/react';

interface TimerState {
  timeLeft: number;
  isActive: boolean;
  isExpired: boolean;
  startTime: number;
  timeout: number;
  requestId: string;
}

type TimerCallback = () => void;

interface TimerInstance {
  requestId: string;
  timeout: number;
  timeoutBuffer: number;
  startTime: number;
  intervalId: NodeJS.Timeout | null;
  onTimeout: TimerCallback | null;
  isExpired: boolean;
}

export class ChatTimerManager {
  private static instance: ChatTimerManager;
  private timers: Map<string, TimerInstance> = new Map();
  private readonly storage =
    typeof window !== 'undefined' ? window.sessionStorage : null;

  private constructor() {}

  static getInstance(): ChatTimerManager {
    if (!ChatTimerManager.instance) {
      ChatTimerManager.instance = new ChatTimerManager();
    }
    return ChatTimerManager.instance;
  }

  /**
   * Initialize or get a timer for a request
   * If timer already exists, returns existing state
   * If not, creates new timer
   */
  initializeTimer(
    requestId: string,
    timeout: number,
    onTimeout?: TimerCallback,
    timeoutBuffer: number = 10,
    autoStart: boolean = true,
  ): TimerState {
    // Check if timer already exists
    const existing = this.timers.get(requestId);
    if (existing) {
      // Update callback if provided
      if (onTimeout) {
        existing.onTimeout = onTimeout;
      }
      return this.getTimerState(requestId);
    }

    // Check for persisted timer data
    const persistedData = this.loadTimerData(requestId);
    if (persistedData) {
      const remaining = this.calculateTimeLeft(
        persistedData.startTime,
        persistedData.timeout,
        timeoutBuffer,
      );

      if (remaining > 0) {
        // Restore existing timer
        const timer: TimerInstance = {
          requestId,
          timeout: persistedData.timeout,
          timeoutBuffer,
          startTime: persistedData.startTime,
          intervalId: null,
          onTimeout: onTimeout || null,
          isExpired: false,
        };

        this.timers.set(requestId, timer);
        this.startTimerInterval(timer);
        this.saveTimerData(timer);
        return this.getTimerState(requestId);
      } else {
        // Timer expired, trigger callback
        if (onTimeout) {
          onTimeout();
        }
        return {
          timeLeft: 0,
          isActive: false,
          isExpired: true,
          startTime: persistedData.startTime,
          timeout: persistedData.timeout,
          requestId,
        };
      }
    }

    // Create new timer
    if (!autoStart) {
      const timer: TimerInstance = {
        requestId,
        timeout,
        timeoutBuffer,
        startTime: Date.now(),
        intervalId: null,
        onTimeout: onTimeout || null,
        isExpired: false,
      };
      this.timers.set(requestId, timer);
      return this.getTimerState(requestId);
    }

    const startTime = Date.now();
    const timer: TimerInstance = {
      requestId,
      timeout,
      timeoutBuffer,
      startTime,
      intervalId: null,
      onTimeout: onTimeout || null,
      isExpired: false,
    };

    this.timers.set(requestId, timer);
    this.startTimerInterval(timer);
    this.saveTimerData(timer);

    return this.getTimerState(requestId);
  }

  /**
   * Get current timer state
   */
  private getTimerState(requestId: string): TimerState {
    const timer = this.timers.get(requestId);
    if (!timer) {
      // Check persisted data
      const persistedData = this.loadTimerData(requestId);
      if (persistedData) {
        const remaining = this.calculateTimeLeft(
          persistedData.startTime,
          persistedData.timeout,
          10, // default buffer
        );
        return {
          timeLeft: Math.max(0, remaining),
          isActive: remaining > 0,
          isExpired: remaining <= 0,
          startTime: persistedData.startTime,
          timeout: persistedData.timeout,
          requestId,
        };
      }
      return {
        timeLeft: 0,
        isActive: false,
        isExpired: true,
        startTime: Date.now(),
        timeout: 0,
        requestId,
      };
    }

    const timeLeft = this.calculateTimeLeft(
      timer.startTime,
      timer.timeout,
      timer.timeoutBuffer,
    );

    return {
      timeLeft: Math.max(0, timeLeft),
      isActive: timer.intervalId !== null && !timer.isExpired,
      isExpired: timer.isExpired || timeLeft <= 0,
      startTime: timer.startTime,
      timeout: timer.timeout,
      requestId,
    };
  }

  /**
   * Start the countdown interval for a timer
   */
  private startTimerInterval(timer: TimerInstance): void {
    if (timer.intervalId) {
      clearInterval(timer.intervalId);
    }

    timer.intervalId = setInterval(() => {
      const remaining = this.calculateTimeLeft(
        timer.startTime,
        timer.timeout,
        timer.timeoutBuffer,
      );

      if (remaining <= 0 && !timer.isExpired) {
        // Timer expired
        timer.isExpired = true;
        if (timer.intervalId) {
          clearInterval(timer.intervalId);
          timer.intervalId = null;
        }
        this.removeTimerData(timer.requestId);

        // Trigger timeout callback
        if (timer.onTimeout) {
          timer.onTimeout();
        }
      }
    }, 1000);
  }

  /**
   * Calculate remaining time
   */
  private calculateTimeLeft(
    startTime: number,
    timeout: number,
    timeoutBuffer: number,
  ): number {
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    return Math.max(0, timeout - timeoutBuffer - elapsed);
  }

  /**
   * Load timer data from storage
   */
  private loadTimerData(requestId: string): {
    startTime: number;
    timeout: number;
  } | null {
    if (!this.storage) return null;

    try {
      const stored = this.storage.getItem(`timer_${requestId}`);
      if (stored) {
        const data = JSON.parse(stored);
        if (data.requestId === requestId) {
          return {
            startTime: data.startTime,
            timeout: data.timeout,
          };
        }
      }
    } catch (error) {
      console.warn('Failed to load timer data:', error);
      Sentry.captureException(error, {
        extra: { context: 'chat-timer-load', requestId },
      });
    }
    return null;
  }

  /**
   * Save timer data to storage
   */
  private saveTimerData(timer: TimerInstance): void {
    if (!this.storage) return;

    try {
      const data = {
        requestId: timer.requestId,
        startTime: timer.startTime,
        timeout: timer.timeout,
      };
      this.storage.setItem(`timer_${timer.requestId}`, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save timer data:', error);
      Sentry.captureException(error, {
        extra: { context: 'chat-timer-save' },
      });
    }
  }

  /**
   * Remove timer data from storage
   */
  private removeTimerData(requestId: string): void {
    if (!this.storage) return;

    try {
      this.storage.removeItem(`timer_${requestId}`);
    } catch (error) {
      console.warn('Failed to remove timer data:', error);
      Sentry.captureException(error, {
        extra: { context: 'chat-timer-remove', requestId },
      });
    }
  }

  /**
   * Clean up all timers (useful for testing or reset)
   */
  cleanup(): void {
    this.timers.forEach((timer) => {
      if (timer.intervalId) {
        clearInterval(timer.intervalId);
      }
    });
    this.timers.clear();
  }
}

// Export singleton instance
export const chatTimerManager = ChatTimerManager.getInstance();
